import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NativeSyntheticEvent, Platform, StyleSheet, TextInput, TextInputKeyPressEventData, View } from 'react-native';

import Text from '../Text';
import i18n from '~/i18n';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_FAMILY_REGULAR, FONT_SIZE_M } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    value: Date | null | undefined;
    onChange: (d: Date | null) => void;
    // Marks the field red without owning the message — the caller renders that.
    hasError?: boolean;
    disabled?: boolean;
};

type Segments = { day: string; month: string; year: string };

const EMPTY: Segments = { day: '', month: '', year: '' };

/**
 * The order and wording of the segments, e.g. "DD.MM.YYYY" — show it next to the field label so
 * nobody has to guess whether the first box is the day or the month.
 */
export const dateFormatHint = () =>
    [i18n.t('date-segment-day'), i18n.t('date-segment-month'), i18n.t('date-segment-year')].join('.');

const digitsOnly = (text: string, max: number) => text.replace(/[^0-9]/g, '').slice(0, max);

// Builds a Date only from a complete, real calendar date — "31.02.1990" stays null rather than
// silently rolling over into March, which is what `new Date(1990, 1, 31)` would do.
const toDate = ({ day, month, year }: Segments): Date | null => {
    if (day.length === 0 || month.length === 0 || year.length !== 4) {
        return null;
    }
    const d = Number(day);
    const m = Number(month);
    const y = Number(year);
    if (!d || !m || !y || m > 12 || d > 31) {
        return null;
    }
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
        return null;
    }
    return date;
};

const toSegments = (date: Date | null | undefined): Segments => {
    if (!date) {
        return EMPTY;
    }
    return {
        day: String(date.getDate()).padStart(2, '0'),
        month: String(date.getMonth() + 1).padStart(2, '0'),
        year: String(date.getFullYear()),
    };
};

const sameDay = (a: Date | null, b: Date | null | undefined) => {
    if (!a || !b) {
        return a == null && b == null;
    }
    return a.getTime() === b.getTime();
};

/**
 * Date entry as three typed segments (DD . MM . YYYY) rather than a calendar.
 *
 * Dates of birth are decades in the past, which makes a calendar grid hostile — it opens on today
 * and every year is another round of navigation. People know their birthday, so typing eight digits
 * is faster and, because this is built from plain RN primitives, it renders identically on web,
 * iOS and Android instead of falling back to three different platform pickers.
 */
export default function DateField({ value, onChange, hasError, disabled }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [segments, setSegments] = useState<Segments>(() => toSegments(value));
    const [focused, setFocused] = useState(false);

    const monthRef = useRef<TextInput>(null);
    const yearRef = useRef<TextInput>(null);
    const dayRef = useRef<TextInput>(null);
    // Focus moves after the segment's new value is committed, never during the change handler —
    // moving it mid-commit lets a fast typist's next keystroke land on a stale input.
    const advanceTo = useRef<React.RefObject<TextInput> | null>(null);

    useEffect(() => {
        if (advanceTo.current) {
            advanceTo.current.current?.focus();
            advanceTo.current = null;
        }
    });

    // Segments are the source of truth while typing; `value` is only adopted when it changes for a
    // reason other than our own onChange (a prefill from the saved customer profile, or a reset).
    // Without the echo guard, clearing one segment emits null, the parent echoes null back, and the
    // resync wipes the segments the user was still editing.
    const emitted = useRef<Date | null>(value ?? null);
    const adopted = useRef(false);

    useEffect(() => {
        if (adopted.current && sameDay(emitted.current, value ?? null)) {
            return;
        }
        adopted.current = true;
        emitted.current = value ?? null;
        setSegments(toSegments(value));
    }, [value]);

    const commit = useCallback(
        (next: Segments) => {
            setSegments(next);
            const date = toDate(next);
            emitted.current = date;
            onChange(date);
        },
        [onChange]
    );

    const onChangeDay = (text: string) => {
        const day = digitsOnly(text, 2);
        commit({ ...segments, day });
        // "5" can only be the 5th, so move on without waiting for a second digit.
        if (day.length === 2 || (day.length === 1 && Number(day) > 3)) {
            advanceTo.current = monthRef;
        }
    };

    const onChangeMonth = (text: string) => {
        const month = digitsOnly(text, 2);
        commit({ ...segments, month });
        if (month.length === 2 || (month.length === 1 && Number(month) > 1)) {
            advanceTo.current = yearRef;
        }
    };

    const onChangeYear = (text: string) => {
        commit({ ...segments, year: digitsOnly(text, 4) });
    };

    // Backspace at the start of a segment steps back into the previous one, so the whole field
    // clears with repeated presses instead of trapping the caret.
    const backspaceTo =
        (ref: React.RefObject<TextInput>, isEmpty: boolean) =>
        (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
            if (e.nativeEvent.key === 'Backspace' && isEmpty) {
                ref.current?.focus();
            }
        };

    const segmentProps = {
        // inputMode covers every platform on its own: react-native maps it to keyboardType
        // 'number-pad', react-native-web sets the DOM inputmode so mobile browsers open the
        // numeric keypad too. Passing keyboardType as well would be dead — inputMode wins.
        inputMode: 'numeric' as const,
        placeholderTextColor: theme.textSecondary,
        selectionColor: theme.button,
        editable: !disabled,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
    };

    return (
        <View
            style={[
                styles.container,
                focused ? styles.containerFocused : null,
                hasError ? styles.containerError : null,
                disabled ? styles.containerDisabled : null,
            ]}
        >
            <TextInput
                {...segmentProps}
                ref={dayRef}
                style={[styles.input, styles.inputNarrow]}
                value={segments.day}
                onChangeText={onChangeDay}
                placeholder={i18n.t('date-segment-day')}
                maxLength={2}
                accessibilityLabel="Day"
            />
            <Text noTranslate style={styles.separator}>
                .
            </Text>
            <TextInput
                {...segmentProps}
                ref={monthRef}
                style={[styles.input, styles.inputNarrow]}
                value={segments.month}
                onChangeText={onChangeMonth}
                onKeyPress={backspaceTo(dayRef, segments.month.length === 0)}
                placeholder={i18n.t('date-segment-month')}
                maxLength={2}
                accessibilityLabel="Month"
            />
            <Text noTranslate style={styles.separator}>
                .
            </Text>
            <TextInput
                {...segmentProps}
                ref={yearRef}
                style={[styles.input, styles.inputWide]}
                value={segments.year}
                onChangeText={onChangeYear}
                onKeyPress={backspaceTo(monthRef, segments.year.length === 0)}
                placeholder={i18n.t('date-segment-year')}
                maxLength={4}
                accessibilityLabel="Year"
            />
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            // Deliberately no alignItems: the segments must stretch to the row height. Centring them
            // leaves Android sizing each input from its own intrinsic height, which clips the digits.
            borderRadius: scaleSize(4),
            borderColor: theme.equityValue,
            borderWidth: 1,
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(12),
            height: scaleSize(44),
        },
        containerFocused: {
            borderColor: theme.button,
        },
        containerError: {
            borderColor: theme.alert,
        },
        containerDisabled: {
            opacity: 0.5,
        },
        input: {
            fontFamily: FONT_FAMILY_REGULAR,
            fontSize: scaleSize(FONT_SIZE_M),
            color: theme.desktopHeaderTextColor,
            textAlign: 'center',
            textAlignVertical: 'center',
            paddingVertical: 0,
            // Removes the focus ring react-native-web paints on top of our own border.
            ...Platform.select({ web: { outlineStyle: 'none' } as object, default: {} }),
        },
        // Sized for the widest placeholder any locale uses, not for the digits: "MM" clips at 30,
        // and the year box has to hold "AAAA" (es/fr) and "ГГГГ" (ru) as well as "YYYY".
        inputNarrow: {
            width: scaleSize(38),
        },
        inputWide: {
            width: scaleSize(62),
        },
        separator: {
            fontSize: FONT_SIZE_M,
            color: theme.textSecondary,
            paddingHorizontal: scaleSize(2),
            alignSelf: 'center',
        },
    });
